import { Box, Dialog, IconButton, Typography } from '@mui/material';
import { styled } from '@mui/material/styles';

import Icon from '../../components/Icon';
import { BRAND } from '../../shared/theme/paletteDark';
import { FONTS } from '../../shared/theme/typography';

/**
 * "About this project".
 *
 * Layout follows "about this project.jpg"; the copy is the demo's, in three
 * short paragraphs: what CommonGround is, that this is NYC with Boston next,
 * and where corrections go. The team is grouped rather than listed flat —
 * founders, then the build team, then mentors — with the blank rows the
 * reference shows between each group.
 */

const CG_LINKEDIN = 'https://www.linkedin.com/company/commongroundorg/';
/** When the city file has not loaded, or names none. */
const FALLBACK_EMAIL = 'hello@joincommonground.app';

/** Where the event data comes from: every listing also links to its own source. */
const DATA_SOURCES: { name: string; href: string }[] = [
  { name: 'NYC Parks', href: 'https://www.nycgovparks.org/events/volunteer' },
  { name: 'Mobilize', href: 'https://www.mobilize.us/' },
  { name: 'LES Ecology Center', href: 'https://www.lesecologycenter.org/' },
  { name: "Randall's Island Park Alliance", href: 'https://randallsisland.org/' },
];

interface Person {
  name: string;
  role: string;
  href?: string;
}

/** Each inner array is one group; groups are separated by a blank row. */
const TEAM: Person[][] = [
  [
    { name: 'Sam Owen', role: 'Co-Founder', href: 'https://www.linkedin.com/in/samuel-owen/' },
    { name: 'Anna Mortara', role: 'Co-Founder', href: 'https://www.linkedin.com/in/anna-mortara/' },
  ],
  [
    { name: 'Hongjin Lin', role: 'Research + Tech Advisor' },
    { name: 'Conner Owen', role: 'Tech Lead (Frontend)', href: 'https://www.linkedin.com/in/chdowen/' },
    { name: 'Danielle Knutson', role: 'Tech Lead (Backend)', href: 'https://www.linkedin.com/in/danielle-knutson' },
    { name: 'Adam Gilbert-Diamond', role: 'Outreach Lead' },
  ],
  [
    { name: 'Eric deMenthon', role: 'Mentor' },
    { name: 'Lauren Tyger', role: 'Mentor' },
  ],
];

const Panel = styled('div')(({ theme }) => ({
  background: BRAND.mochaDeep,
  color: BRAND.parchment,
  padding: '34px 44px 44px',
  display: 'grid',
  gridTemplateColumns: 'minmax(0, 1.55fr) minmax(0, 1fr)',
  gap: 52,
  alignItems: 'start',
  [theme.breakpoints.down('md')]: { gridTemplateColumns: '1fr', gap: 30, padding: '26px 24px 32px' },
}));

/**
 * Drops the right column to the first line of body copy, so the CommonGround
 * mark's top edge lines up with "Hey there!" rather than with the heading —
 * matching "about this project.jpg".
 *
 * The offset is the heading's own height: 31px at line-height 1.15, plus its
 * 24px bottom margin.
 */
const TeamColumn = styled('div')(({ theme }) => ({
  paddingTop: 31 * 1.15 + 24,
  [theme.breakpoints.down('md')]: { paddingTop: 0 },
}));

const Body = styled('p')({
  margin: '0 0 18px',
  fontFamily: FONTS.body,
  fontSize: 14.5,
  lineHeight: 1.62,
  color: 'rgba(243,238,231,0.9)',
  maxWidth: '64ch',
});

const ColHead = styled('h3')({
  margin: '0 0 12px',
  fontFamily: FONTS.display,
  fontWeight: 700,
  fontSize: 18,
  color: BRAND.parchment,
});

const NameRow = styled('div')({
  display: 'grid',
  gridTemplateColumns: 'minmax(0, 1fr) minmax(0, 1.15fr)',
  gap: 10,
  fontFamily: FONTS.body,
  fontSize: 12,
  lineHeight: 1.5,
  padding: '2px 0',
});

const InlineLink = styled('a')({
  color: 'inherit',
  textDecoration: 'underline',
  textUnderlineOffset: 2,
  textDecorationColor: 'rgba(243,238,231,0.4)',
  '&:hover': { textDecorationColor: BRAND.parchment },
});

const LogoLink = styled('a')({
  display: 'block',
  width: 118,
  color: BRAND.parchment,
  transition: 'opacity 120ms ease',
  '&:hover': { opacity: 0.75 },
});

interface Props {
  open: boolean;
  onClose: () => void;
  /** The city file's `correctionsEmail`. */
  correctionsEmail?: string;
}

export default function AboutModal({ open, onClose, correctionsEmail }: Props) {
  const email = correctionsEmail || FALLBACK_EMAIL;
  return (
    <Dialog
      open={open}
      onClose={onClose}
      maxWidth="lg"
      fullWidth
      slotProps={{
        paper: { sx: { background: BRAND.mochaDeep, backgroundImage: 'none', borderRadius: 4 } },
      }}
    >
      <Box sx={{ position: 'relative' }}>
        <IconButton
          onClick={onClose}
          aria-label="Close"
          sx={{ position: 'absolute', top: 14, right: 16, color: BRAND.parchment, zIndex: 1 }}
        >
          ✕
        </IconButton>

        <Panel>
          <div>
            <Typography
              component="h2"
              sx={{
                fontFamily: FONTS.display,
                fontWeight: 700,
                fontSize: 31,
                lineHeight: 1.15,
                mb: 3,
                letterSpacing: '-0.01em',
              }}
            >
              About this project
            </Typography>

            <Body>
              <strong>CommonGround</strong> helps you find your people and do something real for the
              climate, near you: park cleanups, community gardens, tree care, bike counts, talks and
              meetups, gathered in one place and sorted by what you want to do and what you care
              about. Say you're going and it lands in Saved.
            </Body>

            <Body>
              This is a demo for New York City, built from public listings. Boston is next. Anything marked <strong>Sample</strong> is placeholder, there
              to show what the finished product will do.
            </Body>

            <Body>
              Spotted a wrong time, a cancelled event, or something we missed? Tell us at{' '}
              <InlineLink href={`mailto:${email}`}>
                <strong>{email}</strong>
              </InlineLink>
              , or use "Something wrong with this listing?" on any event.
            </Body>

            <Typography
              sx={{
                fontFamily: FONTS.body,
                fontSize: 12.5,
                lineHeight: 1.55,
                color: 'rgba(243,238,231,0.6)',
                mt: 3,
                maxWidth: '64ch',
              }}
            >
              P.S. Your RSVPs are kept in this browser, with no account. Clear your browsing data and
              they go with it.
            </Typography>
          </div>

          <TeamColumn>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.4, mb: 2.2 }}>
              <LogoLink
                href={CG_LINKEDIN}
                target="_blank"
                rel="noopener noreferrer"
                aria-label="CommonGround on LinkedIn"
              >
                <Icon name="logo:cg" size="100%" title="CommonGround" />
              </LogoLink>
              <ColHead style={{ margin: 0 }}>team:</ColHead>
            </Box>

            <Box sx={{ mb: 4 }}>
              {TEAM.map((group, i) => (
                <Box key={group[0].name} sx={{ mt: i === 0 ? 0 : 2 }}>
                  {group.map((person) => (
                    <NameRow key={person.name}>
                      <span style={{ fontWeight: 700 }}>
                        {person.href ? (
                          <InlineLink href={person.href} target="_blank" rel="noopener noreferrer">
                            {person.name}
                          </InlineLink>
                        ) : (
                          person.name
                        )}
                      </span>
                      <span style={{ opacity: 0.75 }}>{person.role}</span>
                    </NameRow>
                  ))}
                </Box>
              ))}
            </Box>

            <ColHead>Event data from:</ColHead>

            <Box>
              {DATA_SOURCES.map((source) => (
                <Typography key={source.name} sx={{ fontFamily: FONTS.body, fontSize: 12.5, fontWeight: 700, mb: 0.4 }}>
                  <InlineLink href={source.href} target="_blank" rel="noopener noreferrer">
                    {source.name}
                  </InlineLink>
                </Typography>
              ))}
              <Typography sx={{ fontFamily: FONTS.body, fontSize: 10.5, opacity: 0.72, mt: 0.6 }}>
                and the organizers listed on each event
              </Typography>
            </Box>
          </TeamColumn>
        </Panel>
      </Box>
    </Dialog>
  );
}
